import { defineStore } from "pinia";
import ApiHelper from "@/helpsers/api.helper";
import authHelper from "@/helpsers/auth.helper";
import axios from "axios";
import { inject, ref, watch } from "vue";
import alertHelper from "@/helpsers/alert.helper";
import * as yup from "yup";

function getProduct({ lists, shoppingListId, roomId, productId }) {
  const list = lists.find((list) => list.id === shoppingListId);
  const room = list.shoppingListRooms.find((room) => room.id === roomId);
  const product = room.shoppingListRoomProducts.find(
    (product) => product.id === productId
  );
  return product;
}
const getValidationMessage = (e) => {
  if (e instanceof yup.ValidationError) {
    if (e.errors.length > 0) {
      const [message] = e.errors;
      return message;
    }
    return null;
  }
  return null;
};

const productSchema = yup.object().shape({
  name: yup.string().required("Nazwa jest wymagana"),
  vendor: yup.string().required("Producent jest wymagany"),
  amount: yup
    .number()
    .typeError("Ilość musi być liczbą")
    .required("Ilość jest wymagana")
    .positive("Ilość musi być większa od zera")
    .integer("Ilość musi być liczbą całkowitą"),
  price: yup
    .number()
    .typeError("Cena jedn. musi być liczbą")
    .required("Cena jedn. jest wymagana")
    .positive("Cena jedn. musi być większa od zera"),
});

export const useShoppingListStore = defineStore("shoppingListStore", () => {
  /** @type {import('vue').Ref<
   * {
   *  id: number;
   *  name: string;
   *  totalPrice: number;
   *  commission: number;
   *  projectId: number;
   *  userId: number;
   *  shoppingListRooms: {
   *    id: number;
   *    shoppingListId: number;
   *    shoppingListRoomProducts: {
   *      id: number;
   *      name: string;
   *      vendor: string;
   *      price: number;
   *      amount: number;
   *      link: string;
   *      imagePath: string;
   *      order: number;
   *      isAcceptedByClient: boolean;
   *      numberOfComments: number;
   *      isLastMessageReadByUser: boolean;
   *      customerAcceptanceStatus: "NONE" | "LIKE" | "DISLIKE";
   *      createdAt: string;
   *      updatedAt: string;
   *      shoppingListRoomId: number;
   *      shoppingListProductImages: {
   *        id: number;
   *        imagePath: string
   *        isMainImage: boolean;
   *        createdAt: string;
   *        updatedAt: string;
   *        shoppingListProductId: number;
   *      }[]
   *      shoppingListProductFiles: {}[];
   *      shoppingListProductComments: {
   *         id: number,
   *         text: string;
   *         isDeleted: boolean;
   *         createdAt: string;
   *         updatedAt: string;
   *         shoppingListProductId: number,
   *         userId: number,
   *         readUsers: [],
   *      }[]
   *     }[]
   *  }[]
   * }[]
   * >}
   *
   */
  const data = ref([]);
  /**
   * Helper to check if user can add/edit another product
   * Allow to edit/add one product at the time
   */
  const isAddingNewProduct = ref(false);
  const projectId = ref(null);
  const refreshing = ref(false);
  const alert = inject("alert");

  watch(projectId, (value) => {
    if (value) {
      getShoppingList();
    } else {
      data.value = [];
      refreshing.value = false;
    }
  });

  async function getShoppingList() {
    try {
      const res = await axios.get(
        ApiHelper.getShoppingListUrl(projectId.value),
        {
          headers: authHelper.authHeader(),
        }
      );
      data.value = res.data;
    } catch (error) {}
  }
  async function refreshShoppingList() {
    refreshing.value = true;
    try {
      const res = await axios.get(
        ApiHelper.getShoppingListUrl(projectId.value),
        {
          headers: authHelper.authHeader(),
        }
      );
      data.value = res.data;
    } catch (error) {
    } finally {
      refreshing.value = false;
    }
  }

  let changeOrderController = null;
  async function changeOrderOfProducts({ shoppingListId, roomId }) {
    if (changeOrderController) {
      changeOrderController.abort();
    }
    changeOrderController = new AbortController();
    try {
      const shoppingList = data.value.find(
        (list) => list.id === shoppingListId
      );
      const room = shoppingList.shoppingListRooms.find(
        (room) => room.id === roomId
      );

      const productsIds = room.shoppingListRoomProducts.map(
        (product) => product.id
      );

      await axios.put(
        ApiHelper.editOrderOfShoppingListProductUrl(
          projectId.value,
          shoppingListId,
          roomId
        ),
        { productsIds },
        {
          headers: authHelper.authHeader(),
          signal: changeOrderController.signal,
        }
      );
    } catch (error) {
      console.error(error);
    }
  }
  async function moveProductToRoom({
    shoppingListId,
    oldRoomId,
    productId,
    newRoomId,
  }) {
    try {
      await axios.put(
        ApiHelper.moveShoppingListProductToAnotherRoomUrl(
          projectId.value,
          shoppingListId,
          oldRoomId,
          productId
        ),
        {
          roomId: newRoomId,
        },
        { headers: authHelper.authHeader() }
      );
      const shoppingList = data.value.find(
        (list) => list.id === shoppingListId
      );
      const room = shoppingList.shoppingListRooms.find(
        (room) => room.id === newRoomId
      );
      const product = room.shoppingListRoomProducts.find(
        (product) => product.id === productId
      );
      product.shoppingListRoomId = newRoomId;
      await changeOrderOfProducts({ shoppingListId, roomId: newRoomId });
    } catch (error) {
      console.log(error);
    }
  }

  async function addRoom({ shoppingListId, name }) {
    try {
      refreshing.value = true;
      await axios.post(
        ApiHelper.addRoomToShoppingListUrl(projectId.value, shoppingListId),
        { name: name },
        { headers: authHelper.authHeader() }
      );
      await getShoppingList();
    } catch (error) {
      console.error(error);
      alertHelper.show(alert, "Błąd dodawania kategorii", "", "error");
    } finally {
      refreshing.value = false;
    }
  }

  async function addProduct({
    shoppingListId,
    roomId,
    data,
    imageFilesToSend,
    filesToSend,
  }) {
    try {
      refreshing.value = true;
      await productSchema.validate(data);
      const res = await axios.post(
        ApiHelper.addProductToShoppingListRoomUrl(
          projectId.value,
          shoppingListId,
          roomId
        ),
        data,
        { headers: authHelper.authHeader() }
      );
      const productId = res.data.id;
      if (imageFilesToSend.length > 0) {
        const formData = new FormData();
        imageFilesToSend.forEach((file) => {
          formData.append("files", file);
        });
        await axios.post(
          ApiHelper.addImagesToShoppingListRoomProductUrl(
            projectId.value,
            shoppingListId,
            roomId,
            productId
          ),
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              ...authHelper.authHeader(),
            },
          }
        );
      }
      if (filesToSend.length > 0) {
        const formData = new FormData();
        filesToSend.forEach((file) => {
          formData.append("files", file);
        });
        await axios.post(
          ApiHelper.addFilesToShoppingListProductUrl(
            projectId.value,
            shoppingListId,
            roomId,
            productId
          ),
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              ...authHelper.authHeader(),
            },
          }
        );
      }
      await getShoppingList();
      refreshing.value = false;
      return { isSuccess: true };
    } catch (error) {
      console.error(error);
      const validationMessage = getValidationMessage(error);
      const message = validationMessage || "Błąd dodawania produktu";
      refreshing.value = false;
      alertHelper.show(alert, message, "", "error");
      return { isSuccess: false };
    }
  }
  /** Helper to check if any other product is being edited */
  function canEditOrAddProduct() {
    if (isAddingNewProduct.value) {
      alertHelper.show(
        alert,
        "Dodawanie produktu",
        "Zakończ dodawanie nowego produktu",
        "error"
      );
      return false;
    }
    for (const list of data.value) {
      for (const room of list.shoppingListRooms) {
        for (const product of room.shoppingListRoomProducts) {
          if (product.isEditing) {
            alertHelper.show(
              alert,
              "Edycja produktu",
              "Zakończ edytowanie poprzedniego produktu",
              "error"
            );
            return false;
          }
        }
      }
    }
    return true;
  }
  async function editProduct({
    shoppingListId,
    roomId,
    productId,
    newData,
    imageFilesToAdd,
    imageFilesToRemove,
    filesToAdd,
    filesToRemove,
  }) {
    try {
      refreshing.value = true;
      await productSchema.validate(newData);
      await axios.put(
        ApiHelper.editShoppingListProductUrl(
          projectId.value,
          shoppingListId,
          roomId,
          productId
        ),
        newData,
        { headers: authHelper.authHeader() }
      );

      // remove images
      if (imageFilesToRemove.length > 0) {
        const removePromises = imageFilesToRemove.map((file) =>
          axios.delete(
            ApiHelper.removeShoppingListProductImageUrl(
              projectId.value,
              shoppingListId,
              roomId,
              productId,
              file.id
            ),
            { headers: authHelper.authHeader() }
          )
        );
        await Promise.all(removePromises);
      }
      // add images
      if (imageFilesToAdd.length > 0) {
        const formData = new FormData();
        imageFilesToAdd.forEach((file) => {
          formData.append("files", file.file);
        });
        await axios.post(
          ApiHelper.addImagesToShoppingListRoomProductUrl(
            projectId.value,
            shoppingListId,
            roomId,
            productId
          ),
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              ...authHelper.authHeader(),
            },
          }
        );
      }

      // remove files
      if (filesToRemove.length > 0) {
        const removePromises = filesToRemove.map((file) =>
          axios.delete(
            ApiHelper.removeFileToShoppingListProductUrl(
              projectId.value,
              shoppingListId,
              roomId,
              productId,
              file.id
            ),
            { headers: authHelper.authHeader() }
          )
        );
        await Promise.all(removePromises);
      }
      // add files
      if (filesToAdd.length > 0) {
        const formData = new FormData();
        filesToAdd.forEach((file) => {
          formData.append("files", file);
        });
        await axios.post(
          ApiHelper.addFilesToShoppingListProductUrl(
            projectId.value,
            shoppingListId,
            roomId,
            productId
          ),
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              ...authHelper.authHeader(),
            },
          }
        );
      }
      await getShoppingList();
      refreshing.value = false;
      return { isSuccess: true };
    } catch (error) {
      const validationMessage = getValidationMessage(error);
      const message = validationMessage || "Błąd edycji produktu";
      alertHelper.show(
        alert,
        message,
        "Nie udało się edytować produktu",
        "error"
      );
      console.error(error);
      refreshing.value = false;
      return { isSuccess: false };
    }
  }
  async function deleteProduct({ shoppingListId, roomId, productId }) {
    refreshing.value = true;
    try {
      await axios.delete(
        ApiHelper.removeProductFromShoppingListUrl(
          projectId.value,
          shoppingListId,
          roomId,
          productId
        ),
        { headers: authHelper.authHeader() }
      );
      await getShoppingList();
    } catch (error) {
      alertHelper.show(
        alert,
        "Błąd usuwania produktu",
        "Nie udało się usunąć produktu",
        "error"
      );
    } finally {
      refreshing.value = false;
    }
  }
  async function deleteRoom({ shoppingListId, roomId }) {
    try {
      refreshing.value = true;
      await axios.delete(
        ApiHelper.deleteShoppingListRoomUrl(
          projectId.value,
          shoppingListId,
          roomId
        ),
        { headers: authHelper.authHeader() }
      );
      await getShoppingList();
    } catch (error) {
      console.error(error);
      alertHelper.show(alert, "Błąd usuwania pomieszczenia", "", "error");
    } finally {
      refreshing.value = false;
    }
  }

  async function editRoomName({ shoppingListId, roomId, name }) {
    refreshing.value = true;
    try {
      await axios.put(
        ApiHelper.editShoppingListRoomUrl(
          projectId.value,
          shoppingListId,
          roomId
        ),
        {
          name: name,
        },
        { headers: authHelper.authHeader() }
      );
      const list = data.value.find((list) => list.id === shoppingListId);
      const room = list.shoppingListRooms.find((room) => room.id === roomId);
      room.name = name;
      refreshing.value = false;
      return { isSuccess: true };
    } catch (error) {
      console.error(error);
      refreshing.value = false;
      alertHelper.show(alert, "Błąd zmiany nazwy", "", "error");
      return { isSuccess: false };
    }
  }
  async function likeProduct({ shoppingListId, roomId, productId }) {
    try {
      await axios.put(
        ApiHelper.editShoppingListProductAcceptanceUrl(
          projectId.value,
          shoppingListId,
          roomId,
          productId
        ),
        {
          customerAcceptanceStatus: "LIKE",
        },
        { headers: authHelper.authHeader() }
      );
      const item = data.value
        .find((list) => list.id === shoppingListId)
        ?.shoppingListRooms.find((room) => room.id === roomId)
        ?.shoppingListRoomProducts.find((product) => product.id === productId);
      item.customerAcceptanceStatus = "LIKE";
    } catch (error) {
      console.error(error);
    }
  }
  async function dislikeProduct({ shoppingListId, roomId, productId }) {
    try {
      await axios.put(
        ApiHelper.editShoppingListProductAcceptanceUrl(
          projectId.value,
          shoppingListId,
          roomId,
          productId
        ),
        {
          customerAcceptanceStatus: "DISLIKE",
        },
        { headers: authHelper.authHeader() }
      );
      const item = data.value
        .find((list) => list.id === shoppingListId)
        ?.shoppingListRooms.find((room) => room.id === roomId)
        ?.shoppingListRoomProducts.find((product) => product.id === productId);
      item.customerAcceptanceStatus = "DISLIKE";
    } catch (error) {
      console.error(error);
    }
  }

  async function noneLikeProduct({ shoppingListId, roomId, productId }) {
    try {
      await axios.put(
        ApiHelper.editShoppingListProductAcceptanceUrl(
          projectId.value,
          shoppingListId,
          roomId,
          productId
        ),
        {
          customerAcceptanceStatus: "NONE",
        },
        { headers: authHelper.authHeader() }
      );
      const item = data.value
        .find((list) => list.id === shoppingListId)
        ?.shoppingListRooms.find((room) => room.id === roomId)
        ?.shoppingListRoomProducts.find((product) => product.id === productId);
      item.customerAcceptanceStatus = "NONE";
    } catch (error) {
      console.error(error);
    }
  }

  async function clearList({ shoppingListId }) {
    refreshing.value = true;
    try {
      await axios.put(
        ApiHelper.clearShoppingListUrl(projectId.value, shoppingListId),
        null,
        { headers: authHelper.authHeader() }
      );
      await getShoppingList();
    } catch (error) {
      console.error(error);
    } finally {
      refreshing.value = false;
    }
  }
  async function editListName({ shoppingListId, name }) {
    refreshing.value = true;
    try {
      const res = await axios.put(
        ApiHelper.editShoppingListUrl(projectId.value, shoppingListId),
        { name },
        { headers: authHelper.authHeader() }
      );
      const list = data.value.find((list) => list.id === shoppingListId);
      list.name = res.data.name;

      refreshing.value = false;
      return { isSuccess: true };
    } catch (error) {
      console.error(error);
      refreshing.value = false;
      return { isSuccess: false };
    }
  }
  async function removeImage({ shoppingListId, roomId, productId, imageId }) {
    refreshing.value = true;
    try {
      await axios.delete(
        ApiHelper.removeShoppingListProductImageUrl(
          projectId.value,
          shoppingListId,
          roomId,
          productId,
          imageId
        ),
        {
          headers: authHelper.authHeader(),
        }
      );
      const updatedProduct = getProduct({
        lists: data.value,
        shoppingListId,
        roomId,
        productId,
      });
      updatedProduct.shoppingListProductImages =
        updatedProduct.shoppingListProductImages.filter(
          (image) => image.id !== imageId
        );
      alertHelper.show(alert, "Usunięto zdjęcie", "");
    } catch (error) {
      console.error(error);
    } finally {
      refreshing.value = false;
    }
  }
  async function addImages({
    shoppingListId,
    roomId,
    productId,
    imageFilesToSend,
  }) {
    refreshing.value = true;
    try {
      const formData = new FormData();
      imageFilesToSend.forEach((file) => {
        formData.append("files", file);
      });
      const res = await axios.post(
        ApiHelper.addImagesToShoppingListRoomProductUrl(
          projectId.value,
          shoppingListId,
          roomId,
          productId
        ),
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...authHelper.authHeader(),
          },
        }
      );
      const updatedProduct = getProduct({
        lists: data.value,
        shoppingListId,
        roomId,
        productId,
      });
      updatedProduct.shoppingListProductImages ||= [];
      updatedProduct.shoppingListProductImages.push(...res.data);
    } catch (error) {
      console.error(error);
    } finally {
      refreshing.value = false;
    }
  }

  /** Comment helpers */
  /** Open this product's comments and close all others product comments */
  function openComments(productId) {
    data.value.forEach((list) => {
      list.shoppingListRooms.forEach((room) => {
        room.shoppingListRoomProducts.forEach((product) => {
          product.commentsOpen = product.id === productId;
        });
      });
    });
  }

  function setProductLastMessageReadByUser(productId) {
    data.value.forEach((list) => {
      list.shoppingListRooms.forEach((room) => {
        room.shoppingListRoomProducts.forEach((product) => {
          if (product.id === productId) {
            product.isLastMessageReadByUser = true;
          }
        });
      });
    });
  }
  function setProductCommentsCount(productId, value) {
    data.value.forEach((list) => {
      list.shoppingListRooms.forEach((room) => {
        room.shoppingListRoomProducts.forEach((product) => {
          if (product.id === productId) {
            product.numberOfComments = value;
          }
        });
      });
    });
  }

  /** Shopping List Cart */
  async function addToCart({ shoppingListId }) {
    try {
      const res = await axios.post(
        ApiHelper.addShoppingListToCartUrl(projectId.value, shoppingListId),
        null,
        { headers: authHelper.authHeader() }
      );
      return res.data;
    } catch (error) {
      console.error(error);
    }
  }

  return {
    data,
    projectId,
    refreshing,
    getShoppingList,
    refreshShoppingList,
    changeOrderOfProducts,
    addRoom,
    addProduct,
    editProduct,
    deleteProduct,
    likeProduct,
    dislikeProduct,
    noneLikeProduct,
    moveProductToRoom,
    deleteRoom,
    editRoomName,
    clearList,
    editListName,
    removeImage,
    addImages,
    canEditOrAddProduct,
    isAddingNewProduct,
    openComments,
    setProductLastMessageReadByUser,
    setProductCommentsCount,
    addToCart,
  };
});
